"use client";

import { useSessionContext } from "@/context/SessionContext";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent } from "@/components/ui/Card";
import { CheckIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { FaRainbow, FaRegSmileBeam, FaUsers } from "react-icons/fa";
import { Button } from "@/components/ui/Button";

const getCopy = (language: "cs" | "en") =>
  ({
    cs: {
      title: "Méně hledání, více rozhodování",
      lead: "Systém je optimalizován pro maximální rychlost. Nově přidané inzeráty jsou okamžitě zařazeny do monitorovacího seznamu - trávíte čas výběrem, ne prohledáváním.",
      benefitsTitle: "Proč to funguje",
      stepsTitle: "Jak začít",
      cta: "Vyzkoušet zdarma - 14 dní bez závazků",
      highlights: [
        {
          icon: <FaUsers />,
          title: "Pro každého",
          text: "Jednotlivci, makléři i investoři — každý najde plán na míru.",
          image: "/image/cta/cta2.png",
        },
        {
          icon: <FaRainbow />,
          title: "Přehledný report",
          text: "Náhledy nemovitostí přímo v mailu nebo ve svém profilu.",
          image: "/image/cta/cta1.png",
        },
        {
          icon: <FaRegSmileBeam />,
          title: "Jednoduché ovládání",
          text: "Nastavení za 2 minuty — žádné technické znalosti nepotřebujete.",
          image: "/image/cta/cta3.png",
        },
      ],
      benefits: [
        "Úspora desítek hodin měsíčně",
        "Monitoring 24 / 7",
        "Okamžité notifikace",
        "Levnější než jedna káva",
      ],
      steps: [
        { action: "Zaregistrujte se", note: "Vyberte úroveň hlídacího psa" },
        {
          action: "Nastavte kritéria",
          note: "Lokalita, cena, velikost a další parametry",
        },
        {
          action: "Hlídač prohledává weby za vás",
          note: "Nepřetržitě, automaticky",
        },
        {
          action: "Dostávejte aktuální nabídky",
          note: "Přímo do mailu dle vašeho plánu",
        },
      ],
    },
    en: {
      title: "Less searching, more deciding",
      lead: "The system is optimized for maximum speed. Newly added listings are instantly added to the monitoring list - you spend time choosing, not searching.",
      benefitsTitle: "Why it works",
      stepsTitle: "How to start",
      cta: "Try free - 14 days with no commitment",
      highlights: [
        {
          title: "For everyone",
          text: "Individuals, agents, and investors can all find the right plan.",
          image: "/image/cta/cta2.png",
        },
        {
          title: "Clear report",
          text: "Property previews directly in email or in your profile.",
          image: "/image/cta/cta1.png",
        },
        {
          title: "Simple controls",
          text: "Setup in 2 minutes - no technical knowledge needed.",
          image: "/image/cta/cta3.png",
        },
      ],
      benefits: [
        "Save dozens of hours every month",
        "24 / 7 monitoring",
        "Instant notifications",
        "Cheaper than one coffee",
      ],
      steps: [
        { action: "Register", note: "Choose your watchdog level" },
        {
          action: "Set criteria",
          note: "Location, price, size, and other parameters",
        },
        {
          action: "The watchdog searches sites for you",
          note: "Continuously and automatically",
        },
        {
          action: "Receive current listings",
          note: "Straight to your email based on your plan",
        },
      ],
    },
  })[language];

const SomeWatchDogInfo = () => {
  const { setSelectedProduct } = useSessionContext();
  const { language } = useLanguage();
  const copy = getCopy(language);

  return (
    <div className="watchdog-info-root">
      <div className="max-w-2xl mx-auto text-center space-y-3">
        <h4 className="app-content-title">{copy.title}</h4>
        <p className="app-content-description">{copy.lead}</p>
      </div>

      {/* Feature cards */}
      <div className="watchdog-features-grid">
        {copy.highlights.map((feat, i) => (
          <div key={i} className="watchdog-feature-card">
            <Image
              src={feat.image}
              width={600}
              height={400}
              alt={feat.title}
              className="watchdog-feature-img"
            />
            <h4 className="watchdog-feature-title">{feat.title}</h4>
            <p className="watchdog-feature-text">{feat.text}</p>
          </div>
        ))}
      </div>

      {/* Benefits + Steps side by side */}
      <div className="watchdog-bottom-grid">
        <div className="watchdog-benefits">
          <h4 className="watchdog-sub-heading">{copy.benefitsTitle}</h4>
          <div className="watchdog-benefits-list">
            {copy.benefits.map((b, i) => (
              <Card
                key={i}
                className="group mt-0 w-full max-w-[28rem] bg-background/95 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md border-0"
              >
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-softiq-primary text-white">
                    <CheckIcon className="size-5" />
                  </div>
                  <span className="text-sm font-medium leading-6 text-foreground sm:text-base">
                    {b}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <div className="watchdog-benefits">
          <h4 className="watchdog-sub-heading">{copy.stepsTitle}</h4>
          <div className="watchdog-benefits-list">
            {copy.steps.map((step, i) => (
              <Card
                key={i}
                className="group mt-0 w-full max-w-[28rem] bg-background/95 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md border-0"
              >
                <CardContent className="flex items-center gap-4 p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-softiq-primary text-sm font-semibold text-white shadow-sm">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="watchdog-step-action">{step.action}</p>
                    {step.note && (
                      <p className="watchdog-step-note">{step.note}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <Button asChild variant="default" className="w-fit mx-auto">
        <Link href="/#cenik" onClick={() => setSelectedProduct?.("BASIC")}>
          {copy.cta}
        </Link>
      </Button>
    </div>
  );
};

export default SomeWatchDogInfo;
